import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import moment from "moment";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import "./ResumenVuelosIntegrados.css";
import './SharedLoading.css';
import API_BASE_URL from "../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ModalApoyoVuelo from "../components/ModalApoyoVuelo";
import RegistradosDLTab from "../components/RegistradosDLTab";

// 🔹 Logos por aerolínea
const airlineLogos = {
  AM: require("../assets/aeromexico.webp"),
  DL: require("../assets/delta.png"),
  AA: require("../assets/AA.jpeg"),
  UA: require("../assets/UA.jpeg"),
  AC: require("../assets/airCanada.jpeg"),
  IB: require("../assets/iberia.png"),
  CZ: require("../assets/chinaSouth.png"),
  WS: require("../assets/westjet.png"),
  ANA: require("../assets/ana.png"),
  AV: require("../assets/avianca.png"),
  TA: require("../assets/taca.png"),
  LR: require("../assets/lacsa.jpeg"),
  LH: require("../assets/lufthansa.png"),
  EK: require("../assets/emirates.png"),
  HU: require("../assets/hainan.png"),
  LA: require("../assets/latam.png"),
  TK: require("../assets/turkish.jpg"),
  FLE: require("../assets/flair.png"),
  F8: require("../assets/flair.png"),
  TS: require("../assets/airTransat.png"),
  BA: require("../assets/british.jpeg"),
  YQ: require("../assets/tar.png"),
  WG: require("../assets/sunwing.png"),
  F9: require("../assets/frontier.jpeg"),
  PD: require("../assets/porter.jpeg"),
  UJ: require("../assets/magni.jpeg"),
  MX: require("../assets/breeze.png"),
  WK: require("../assets/edelweiss.png"),
  "4Y": require("../assets/discover.jpeg"),


  // Agrega más aerolíneas y sus logos según sea necesario
};

const ResumenVuelosPage = () => {
  const token = useSelector((state) => state.auth.token);
  const decoded = token ? jwtDecode(token) : null;

  const [tabActiva, setTabActiva] = useState('vuelos'); // 'vuelos' | 'registrados' (Delta)
  const [loadingPage, setLoadingPage] = useState(true);
  const [vuelos, setVuelos] = useState([]);

  // 🔹 Estados de modales
  const [vueloSeleccionado, setVueloSeleccionado] = useState(null);

  // Operadores
  const [modalOperadoresAbierto, setModalOperadoresAbierto] = useState(false);
  const [colaboradores, setColaboradores] = useState([]);
  const [operadoresSeleccionados, setOperadoresSeleccionados] = useState([]);
  const [sillasOperadores, setSillasOperadores] = useState({});
  const [busquedaSiglas, setBusquedaSiglas] = useState("");
  const [registrando, setRegistrando] = useState(false);
  const [apoyoVuelo, setApoyoVuelo] = useState(null); // vuelo para el modal de apoyo

  // Servicios en sistema
  const [modalServiciosAbierto, setModalServiciosAbierto] = useState(false);
  const [vueloServiciosSeleccionado, setVueloServiciosSeleccionado] = useState(null);
  const [cantidadServicios, setCantidadServicios] = useState("");
  const [asientosServicios, setAsientosServicios] = useState([]);
  const [registroExistente, setRegistroExistente] = useState(null);
  const [modoSobrescribir, setModoSobrescribir] = useState(false);

  const [busquedaVuelo, setBusquedaVuelo] = useState("");
  // Mapa de servicios por vuelo validados por fecha (solo hoy)
  const [serviciosPorVuelo, setServiciosPorVuelo] = useState({});

  const estacionUsuario = decoded?.estacion?.trim().toUpperCase();
  const estacionVuelo = (vuelo) =>
    (vuelo?.estacion ?? vuelo?.Estacion ?? vuelo?.station ?? vuelo?.stationCode ?? "")
      .trim()
      .toUpperCase() || null;

  const claseTiempo = (hora) => {
    const ahora = moment();
    const t = moment(hora);
    const diffMin = t.diff(ahora, "minutes");
    if (diffMin >= 0 && diffMin <= 15) return "cercano-15";
    if (diffMin <= 30) return "cercano-30";
    if (diffMin <= 60) return "cercano-60";
    return "";
  };

  const obtenerBadgeHora = (hora) => {
    const ahora = moment();
    const t = moment(hora);
    const diffMin = t.diff(ahora, "minutes");
    let clase = "dot-normal";
    if (diffMin >= 0 && diffMin <= 15) clase = "dot-urgente";
    else if (diffMin <= 30) clase = "dot-alerta";
    return (
      <span className="badge-hora">
        <span className={`badge-dot ${clase}`} />
        {t.format("HH:mm")}
      </span>
    );
  };

  // 🔹 Cargar vuelos de todas las aerolíneas
  const fetchData = async () => {
    try {
      const endpoints = [ "hoyIntegrado"];
      const responses = await Promise.all(
        endpoints.map((ep) =>
          axios.get(`${API_BASE_URL}api/vuelos/${ep}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );

      const todosVuelos = responses.flatMap((res, idx) => {
        const airline = endpoints[idx].replace("hoy", "");
        return res.data.map((v) => ({ ...v, airline }));
      });

      let filtrados = todosVuelos;

      if (estacionUsuario) {
        filtrados = todosVuelos.filter(
          (v) =>
            estacionVuelo(v) === estacionUsuario
        );
      }

      filtrados.sort((a, b) => {
        const horaA =
          a.tipoServicio === "LLEGADAS"
            ? moment(a.published_arrival_time)
            : moment(a.published_departure_time);
        const horaB =
          b.tipoServicio === "LLEGADAS"
            ? moment(b.published_arrival_time)
            : moment(b.published_departure_time);
        return horaA.diff(horaB);
      });

      setVuelos(filtrados);

      // Cargar conteo de servicios vigente solo para hoy
      try {
        const hoy = moment().format("YYYY-MM-DD");
        const results = await Promise.all(
          filtrados.map(async (v) => {
            try {
              const res = await axios.get(
                `${API_BASE_URL}api/vuelos/servicios-vuelo`,
                {
                  params: { idVuelo: v._id },
                  headers: { Authorization: `Bearer ${token}` },
                }
              );
              const reg = res.data;
              if (reg && reg.fechaInput === hoy && Number.isFinite(Number(reg.cantidadServicios))) {
                return { id: v.id, cantidad: Number(reg.cantidadServicios), fechaInput: reg.fechaInput };
              }
              return { id: v.id, cantidad: null, fechaInput: reg?.fechaInput };
            } catch (_) {
              return { id: v.id, cantidad: null };
            }
          })
        );
        const map = {};
        for (const r of results) {
          if (r.cantidad != null) map[r.id] = { cantidad: r.cantidad, fechaInput: r.fechaInput };
        }
        setServiciosPorVuelo(map);
      } catch (_) {
        // silencioso
      }
    } catch (err) {
      console.error("Error al cargar vuelos:", err);
    } finally {
      setLoadingPage(false);
    }
  };

  // 🔹 Abrir modal operadores
  const abrirModalOperadores = async (vuelo) => {
    try {
      const res = await axios.get(`${API_BASE_URL}api/bitacora/en-turno-detalles`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const disponibles = res.data.filter(
        (c) => c.estacion?.trim().toUpperCase() === decoded?.estacion?.trim().toUpperCase()
      );

      setColaboradores(disponibles);
      setVueloSeleccionado(vuelo);
      setOperadoresSeleccionados([]);
      setModalOperadoresAbierto(true);
    } catch (error) {
      toast.error("Error al obtener operadores");
    }
  };

  // 🔹 Registrar servicios (desde modal operadores)
  const registrarServiciosVuelo = async () => {
    if (registrando) return;
    if (operadoresSeleccionados.length === 0) {
      toast.warning("⚠️ Debes seleccionar al menos un operador");
      return;
    }

    setRegistrando(true);
    try {
      for (const operador of operadoresSeleccionados) {
        const sillaSeleccionada = sillasOperadores[operador.noColaborador] || "WCHR";

        const payload = {
          nombrePax: "",
          aerolinea: vueloSeleccionado.aerolinea,
          noVuelo: vueloSeleccionado.flight_number,
          noAsiento: "S/A",
          origenVuelo: vueloSeleccionado.latest_departure_airport,
          destinoVuelo: vueloSeleccionado.latest_arrival_airport,
          fechaInput: moment().format("YYYY-MM-DD"),
          horaInicio: moment().format("YYYY-MM-DD HH:mm:ss"),
          estacion: decoded?.estacion?.trim().toUpperCase(),
          usuarioInicio: decoded?.id,
          noColaborador: operador.noColaborador,
          statusServicio: "ASIGNADO",
          tipoVuelo: vueloSeleccionado.tipoServicio,
          int_nac: vueloSeleccionado.tipoVuelo,
          pnr: `${vueloSeleccionado.flight_number}-${operador.noColaborador}-${Date.now()}`,
          tipoSilla: sillaSeleccionada,
          conexion: "Si",
          uh: "UH",
          horaVuelo: moment(
            vueloSeleccionado.tipoServicio === "LLEGADAS"
              ? vueloSeleccionado.published_arrival_time
              : vueloSeleccionado.published_departure_time
          ).format("YYYY-MM-DD HH:mm:ss"),
          sala: vueloSeleccionado.sala || null,
        };

        await axios.post(`${API_BASE_URL}api/servicios/registrar`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      toast.success("✅ Servicios registrados correctamente");
      setModalOperadoresAbierto(false);
    } catch (error) {
      console.error("❌ Error al registrar servicios:", error);
      toast.error("Error al registrar servicios");
    } finally {
      setRegistrando(false);
    }
  };

  // 🔹 Actualizar hora editable
  const actualizarHoraVuelo = async (vuelo, nuevaHora) => {
    try {
      await axios.put(
        `${API_BASE_URL}api/vuelos/actualizar-hora`,
        { idVuelo: vuelo.id, tipo: vuelo.tipoServicio, nuevaHora },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setVuelos((prev) =>
        prev.map((v) =>
          v.id === vuelo.id
            ? {
                ...v,
                published_arrival_time:
                  vuelo.tipoServicio === "LLEGADAS"
                    ? moment(v.published_arrival_time)
                        .set({
                          hour: nuevaHora.split(":")[0],
                          minute: nuevaHora.split(":")[1],
                        })
                        .toISOString()
                    : v.published_arrival_time,
                published_departure_time:
                  vuelo.tipoServicio === "SALIDAS"
                    ? moment(v.published_departure_time)
                        .set({
                          hour: nuevaHora.split(":")[0],
                          minute: nuevaHora.split(":")[1],
                        })
                        .toISOString()
                    : v.published_departure_time,
              }
            : v
        )
      );

      toast.success("✅ Hora actualizada");
    } catch (err) {
      toast.error("No se pudo actualizar hora");
    }
  };

  // 🔹 Actualizar sala editable
  const actualizarSalaVuelo = async (vuelo, nuevaSala) => {
    try {
      await axios.put(
        `${API_BASE_URL}api/vuelos/actualizar-sala`,
        { idVuelo: vuelo.id, sala: nuevaSala },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setVuelos((prev) =>
        prev.map((v) => (v.id === vuelo.id ? { ...v, sala: nuevaSala } : v))
      );

      toast.success("✅ Sala actualizada");
    } catch (err) {
      toast.error("No se pudo actualizar sala");
    }
  };

  // 🔹 Abrir modal Servicios en sistema
  const abrirModalServicios = (vuelo) => {
    setVueloServiciosSeleccionado(vuelo);
    setCantidadServicios("");
    setAsientosServicios([]);
    setModalServiciosAbierto(true);
  };

  const handleClickServicios = async (vuelo) => {
    try {
      const hoy = moment().format("YYYY-MM-DD");
      let reg = null;
      try {
        const res = await axios.get(`${API_BASE_URL}api/vuelos/servicios-vuelo`, {
          params: { idVuelo: vuelo._id },
          headers: { Authorization: `Bearer ${token}` },
        });
        reg = res.data || null;
      } catch (_) {
        reg = null;
      }

      setVueloServiciosSeleccionado(vuelo);
      setModalServiciosAbierto(true);
      setModoSobrescribir(false);
      setRegistroExistente(reg && reg.fechaInput === hoy ? reg : null);

      if (reg && reg.fechaInput === hoy) {
        setCantidadServicios(String(reg.cantidadServicios || reg.asientos?.length || ""));
        setAsientosServicios(Array.isArray(reg.asientos) ? reg.asientos : []);
        toast.info("Se cargó el registro existente de hoy. Guarda para sobreescribir o crea uno nuevo.");
      } else {
        setCantidadServicios("");
        setAsientosServicios([]);
      }
    } catch (err) {
      abrirModalServicios(vuelo);
    }
  };

  // 🔹 Manejar cantidad/asientos
  const onChangeCantidadServicios = (valor) => {
    const num = String(valor).replace(/[^0-9]/g, "");
    setCantidadServicios(num);
    const n = parseInt(num || "0", 10);
    if (Number.isNaN(n) || n < 0) return;
    setAsientosServicios(Array(n).fill(""));
  };

  const enviarServiciosSistema = async () => {
    const n = parseInt(cantidadServicios || "0", 10);
    if (!vueloServiciosSeleccionado || !n || n <= 0) return;

    const payload = {
      idVuelo: vueloServiciosSeleccionado._id,
      noVuelo: vueloServiciosSeleccionado.flight_number,
      fechaInput: moment().format("YYYY-MM-DD"),
      cantidadServicios: n,
      asientos: asientosServicios,
      estacion: estacionUsuario || estacionVuelo(vueloServiciosSeleccionado),
    };

    const postServicios = async (overwrite = false) => {
      const body = overwrite ? { ...payload, overwrite: true } : payload;
      return axios.post(`${API_BASE_URL}api/vuelos/servicios-vuelo`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
    };

    try {
      await postServicios(modoSobrescribir);
      setServiciosPorVuelo((prev) => ({
        ...prev,
        [vueloServiciosSeleccionado.id]: { cantidad: n, fechaInput: payload.fechaInput },
      }));
      toast.success(modoSobrescribir ? "✅ Registro sobrescrito correctamente" : "✅ Servicios en sistema guardados");
      setModalServiciosAbierto(false);
      setRegistroExistente(null);
      setModoSobrescribir(false);
    } catch (err) {
      if (err.response?.status === 409) {
        const confirmar = window.confirm(
          "Ya existe un registro para este vuelo en esa fecha. ¿Deseas sobrescribirlo?"
        );
        if (!confirmar) return;
        try {
          await postServicios(true);
          setServiciosPorVuelo((prev) => ({
            ...prev,
            [vueloServiciosSeleccionado.id]: { cantidad: n, fechaInput: payload.fechaInput },
          }));
          toast.success("✅ Registro sobrescrito correctamente");
          setModalServiciosAbierto(false);
        } catch (err2) {
          console.error("❌ Error al sobrescribir servicios:", err2);
          toast.error("No se pudo sobrescribir servicios en sistema");
        }
      } else {
        console.error("❌ Error al registrar servicios en sistema:", err);
        toast.error("No se pudo actualizar servicios en sistema");
      }
    }
  };

  const agregarAsiento = () => {
    setAsientosServicios((prev) => [...prev, ""]);
    setCantidadServicios((prev) => {
      const num = parseInt(prev || "0", 10) || 0;
      return String(num + 1);
    });
  };

  // 🔹 Render tabla
  const renderTabla = () => {
    const vuelosFiltrados = vuelos.filter((v) =>
      v.flight_number.toString().toUpperCase().includes(busquedaVuelo.toUpperCase())
    );

    return (
      <div className="tabla-resumen">
        <div className="tabla-header-row rv-head-integrado">
          <div className="tabla-header-info">
            <span className="tabla-header-titulo">✈  Vuelos integrados</span>
            <span className="tabla-header-count">
              {vuelosFiltrados.length} vuelo{vuelosFiltrados.length !== 1 ? "s" : ""} ·{" "}
              {vuelos.filter((v) => v.tipoServicio === "LLEGADAS").length} llegadas ·{" "}
              {vuelos.filter((v) => v.tipoServicio === "SALIDAS").length} salidas
            </span>
          </div>
        </div>

        <div className="tabla-search-area">
          <div className="rv-search-wrapper">
            <span className="rv-search-icon">⌕</span>
            <input
              type="text"
              placeholder="Buscar vuelo..."
              className="input-buscador-vuelo"
              value={busquedaVuelo}
              onChange={(e) => setBusquedaVuelo(e.target.value)}
            />
          </div>
        </div>

        <div className="scroll-wrapper-integrado">
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Aerolínea</th>
                <th>Vuelo</th>
                <th>Ruta</th>
                <th>Dirección</th>
                <th>Hora</th>
                <th>Servicios</th>
                <th>Tipo</th>
                <th>Sala</th>
                <th>Apoyo</th>
              </tr>
            </thead>
            <tbody>
              {vuelosFiltrados.map((vuelo, idx) => {
                const horaVuelo =
                  vuelo.tipoServicio === "LLEGADAS"
                    ? moment(vuelo.published_arrival_time)
                    : moment(vuelo.published_departure_time);

                const claseFila =
                  vuelo.tipoServicio === "LLEGADAS"
                    ? claseTiempo(vuelo.published_arrival_time)
                    : claseTiempo(vuelo.published_departure_time);

                const serviciosHoy = serviciosPorVuelo[vuelo.id];

                return (
                  <tr
                    key={`${vuelo.aerolinea}-${vuelo.id}`}
                    className={claseFila}
                    onClick={() => abrirModalOperadores(vuelo)}
                    style={{ cursor: "pointer" }}
                  >
                    <td className="rv-idx">{idx + 1}</td>
                    <td>
                      <img
                        src={airlineLogos[vuelo.aerolinea]}
                        alt={vuelo.aerolinea}
                        className="rv-airline-logo"
                      />
                    </td>
                    <td>
                      <span className="rv-flight-num">{vuelo.flight_number}</span>
                    </td>
                    <td>
                      <div className="rv-ruta">
                        <span className="rv-iata">{vuelo.latest_departure_airport}</span>
                        <span className="rv-arrow">→</span>
                        <span className="rv-iata">{vuelo.latest_arrival_airport}</span>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`rv-dir-badge ${
                          vuelo.tipoServicio === "LLEGADAS" ? "rv-dir-llegada" : "rv-dir-salida"
                        }`}
                      >
                        {vuelo.tipoServicio === "LLEGADAS" ? "↓ LLD" : "↑ SAL"}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="time"
                        defaultValue={horaVuelo.format("HH:mm")}
                        className="input-time-vuelo"
                        onBlur={(e) => actualizarHoraVuelo(vuelo, e.target.value)}
                      />
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <span
                        className={`rv-servicio-pill ${
                          serviciosHoy?.cantidad != null ? "rv-servicio-ok" : "rv-servicio-empty"
                        }`}
                        onClick={() => handleClickServicios(vuelo)}
                        style={{ cursor: "pointer" }}
                      >
                        {serviciosHoy?.cantidad != null ? serviciosHoy.cantidad : "—"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`rv-tipo-badge ${
                          vuelo.tipoVuelo === "NACIONAL" ? "rv-tipo-nac" : "rv-tipo-int"
                        }`}
                      >
                        {vuelo.tipoVuelo === "NACIONAL" ? "NAC" : "INT"}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        defaultValue={vuelo.sala || ""}
                        className="input-sala editable"
                        onFocus={(e) => {
                          e.target.dataset.original = e.target.value;
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => {
                          const nuevoValor = e.target.value.trim();
                          const valorOriginal = e.target.dataset.original?.trim();
                          if (nuevoValor !== valorOriginal)
                            actualizarSalaVuelo(vuelo, nuevoValor);
                        }}
                      />
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {vuelo.aerolinea !== "AM" && (
                        <button
                          className="btn-apoyo-vuelo"
                          onClick={() => setApoyoVuelo(vuelo)}
                          title="Asignar operador de apoyo (no genera noshow)"
                        >
                          + Apoyo
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };


  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  if (loadingPage) {
    return (
      <div className="resumen-vuelos-container">
        <div className="page-loading-overlay">
          <div className="page-loading-spinner" />
          <p className="page-loading-text">Cargando resumen de vuelos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="resumen-vuelos-container">
      <div className="rv-page-header">
        <div className="rv-page-header-left">
          <h1 className="rv-page-title">Vuelos integrados</h1>
          <p className="rv-page-subtitle">
            {decoded?.estacion?.toUpperCase()} · {moment().format("dddd D [de] MMMM [de] YYYY")}
          </p>
        </div>
        <div className="rv-stats">
          <div className="rv-stat-item rv-stat-llegadas">
            <span className="rv-stat-num">
              {vuelos.filter((v) => v.tipoServicio === "LLEGADAS").length}
            </span>
            <span className="rv-stat-label">↓ Llegadas</span>
          </div>
          <div className="rv-stat-divider" />
          <div className="rv-stat-item rv-stat-salidas">
            <span className="rv-stat-num">
              {vuelos.filter((v) => v.tipoServicio === "SALIDAS").length}
            </span>
            <span className="rv-stat-label">↑ Salidas</span>
          </div>
        </div>
      </div>

      <div className="dl-tabs" role="tablist" aria-label="Sección Vuelos integrados">
        <button
          role="tab"
          aria-selected={tabActiva === 'vuelos'}
          className={`dl-tab ${tabActiva === 'vuelos' ? 'dl-tab-active' : ''}`}
          onClick={() => setTabActiva('vuelos')}
        >
          <span className="dl-tab-icon" aria-hidden>✈️</span> Vuelos
        </button>
        <button
          role="tab"
          aria-selected={tabActiva === 'registrados'}
          className={`dl-tab ${tabActiva === 'registrados' ? 'dl-tab-active' : ''}`}
          onClick={() => setTabActiva('registrados')}
        >
          <span className="dl-tab-icon" aria-hidden>📋</span> Registrados <span className="dl-tab-badge">DL</span>
        </button>
      </div>

      {tabActiva === 'vuelos' && renderTabla()}
      {tabActiva === 'registrados' && (
        <RegistradosDLTab
          estacion={decoded?.estacion?.trim().toUpperCase()}
          token={token}
          decoded={decoded}
        />
      )}

      {/* Modal operadores */}
      {modalOperadoresAbierto && (
        <div className="modal-overlay-pasajeros">
          <div className="modal-pasajeros modal-operadores">
            <div className="modal-operadores__header">
              <div>
                <p className="modal-operadores__label">Asignar operadores</p>
                <h3 className="modal-operadores__title">
                  Vuelo {vueloSeleccionado?.flight_number} · {vueloSeleccionado?.latest_departure_airport} →{" "}
                  {vueloSeleccionado?.latest_arrival_airport}
                </h3>
              </div>
              <button className="modal-close-btn" onClick={() => setModalOperadoresAbierto(false)}>
                ✖
              </button>
            </div>

            <div className="modal-operadores__controls">
              <div className="buscador-mini">
                <span className="icono-lupa">🔍</span>
                <input
                  type="text"
                  placeholder="Buscar por siglas..."
                  value={busquedaSiglas}
                  onChange={(e) => setBusquedaSiglas(e.target.value.toUpperCase())}
                />
              </div>
              <div className="modal-operadores__hint">Selecciona operadores y tipo de silla</div>
            </div>

            <div className="modal-operadores__table-wrapper">
              <table className="tabla-operadores">
                <thead>
                  <tr>
                    <th></th>
                    <th>#</th>
                    <th>Nombre</th>
                    <th>Siglas</th>
                    <th>Tipo de Silla</th>
                  </tr>
                </thead>
                <tbody>
                  {colaboradores
                    .filter((c) => c.siglas.toUpperCase().includes(busquedaSiglas))
                    .map((c, idx) => (
                      <tr key={c.noColaborador}>
                        <td>
                          <input
                            type="checkbox"
                            checked={operadoresSeleccionados.some((op) => op.noColaborador === c.noColaborador)}
                            onChange={(e) =>
                              e.target.checked
                                ? setOperadoresSeleccionados((prev) => [...prev, c])
                                : setOperadoresSeleccionados((prev) =>
                                    prev.filter((op) => op.noColaborador !== c.noColaborador)
                                  )
                            }
                          />
                        </td>
                        <td>{idx + 1}</td>
                        <td>{c.nombreCompleto}</td>
                        <td>{c.siglas}</td>
                        <td>
                          <select
                            value={sillasOperadores[c.noColaborador] || "WCHR"}
                            onChange={(e) =>
                              setSillasOperadores((prev) => ({
                                ...prev,
                                [c.noColaborador]: e.target.value,
                              }))
                            }
                          >
                            <option value="WCHR">WCHR</option>
                            <option value="WCHS">WCHS</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div className="modal-operadores__actions">
              <button className="btn-registrar" onClick={registrarServiciosVuelo} disabled={registrando}>
                {registrando ? "Registrando..." : "Registrar servicios"}
              </button>
              <button className="btn-cerrar" onClick={() => setModalOperadoresAbierto(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal servicios en sistema */}
      {modalServiciosAbierto && (
        <div className="modal-overlay-pasajeros">
          <div className="modal-pasajeros modal-servicios">
            <div className="modal-operadores__header">
              <div>
                <p className="modal-operadores__label">Servicios en sistema</p>
                <h3 className="modal-operadores__title">
                  Vuelo {vueloServiciosSeleccionado?.flight_number}
                </h3>
                {registroExistente && (
                  <p className="modal-servicios__alerta">
                    Ya existe un registro de hoy. Puedes editarlo o crear uno nuevo para sobrescribir.
                  </p>
                )}
              </div>
              <button className="modal-close-btn" onClick={() => setModalServiciosAbierto(false)}>
                ✖
              </button>
            </div>

            <div className="modal-servicios__controls">
              <div className="campo">
                <label>Cantidad de servicios</label>
                <input
                  type="number"
                  min={1}
                  value={cantidadServicios}
                  onChange={(e) => onChangeCantidadServicios(e.target.value)}
                  placeholder="Ej. 3"
                />
              </div>
              <div className="campo">
                <label>Acciones</label>
                <div className="acciones-inline">
                  <button type="button" className="btn-secundario" onClick={agregarAsiento}>
                    + Asiento
                  </button>
                  <button
                    type="button"
                    className="btn-secundario"
                    onClick={() => {
                      setModoSobrescribir(true);
                      setCantidadServicios("");
                      setAsientosServicios([]);
                      setRegistroExistente(null);
                    }}
                  >
                    Crear nuevo registro
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-servicios__asientos">
              {asientosServicios.map((val, idx) => (
                <div key={idx} className="asiento-item">
                  <span className="chip-asiento">{idx + 1}</span>
                  <input
                    type="text"
                    value={val}
                    onChange={(e) => {
                      const copia = [...asientosServicios];
                      copia[idx] = e.target.value.toUpperCase();
                      setAsientosServicios(copia);
                    }}
                    placeholder={`Asiento ${idx + 1}`}
                  />
                </div>
              ))}
              {asientosServicios.length === 0 && (
                <p className="texto-vacio">Sin asientos cargados. Usa "+ Asiento" para agregar.</p>
              )}
            </div>

            <div className="modal-operadores__actions">
              <button className="btn-registrar" onClick={enviarServiciosSistema}>
                Guardar
              </button>
              <button className="btn-cerrar" onClick={() => setModalServiciosAbierto(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      <ModalApoyoVuelo
        vuelo={apoyoVuelo}
        onClose={() => setApoyoVuelo(null)}
      />

      <ToastContainer />
    </div>
  );
};

export default ResumenVuelosPage;
